/**
 * Table templates (owner 2026-09-27 "a table template"): named column sets the user saves from a
 * table and applies to any instance of the same table. User-level, stored in the synced settings
 * document under `table_templates` (SPEC §18 "User" scope), so every device sees them. A panel's
 * own column state (panel params) records only which template it last applied.
 *
 * Tradovate and NinjaTrader both save column layouts by name (see docs/DESIGN-REFERENCE.md
 * "Table columns"); saving under an existing name overwrites it, as Tradovate's workspace
 * templates do.
 */
import {
  COLUMN_STATE_VERSION,
  sanitizeColumnState,
  type ColumnState,
  type TableDef,
  type TableId,
} from "./columns.ts";

export const TABLE_TEMPLATES_VERSION = 1;
export const MAX_TEMPLATES_PER_TABLE = 20;
export const MAX_TEMPLATE_NAME = 40;

export type TableTemplate = {
  id: string;
  table: TableId;
  name: string;
  /** Visible columns in order. */
  columns: string[];
  /** Non-default widths (px). */
  widths: Record<string, number>;
};

export type TableTemplatesDoc = {
  v: typeof TABLE_TEMPLATES_VERSION;
  items: TableTemplate[];
};

export const EMPTY_TABLE_TEMPLATES: TableTemplatesDoc = { v: TABLE_TEMPLATES_VERSION, items: [] };

const TABLE_IDS: ReadonlySet<string> = new Set<TableId>(["positions", "accounts", "orders", "fills"]);

export function cleanTemplateName(name: string): string {
  return name.replace(/\s+/g, " ").trim().slice(0, MAX_TEMPLATE_NAME).trim();
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Validate the synced value. `defs` gives each table's registry so column ids are checked the
 * same way as panel state (unknown ids dropped, locked restored, widths clamped); a template left
 * with no columns is dropped. Unknown tables and duplicate ids are dropped. Never throws.
 */
export function sanitizeTableTemplates(
  raw: unknown,
  defs: Partial<Record<TableId, TableDef<never>>>,
): TableTemplatesDoc {
  if (!isRecord(raw) || !Array.isArray(raw["items"])) return EMPTY_TABLE_TEMPLATES;
  const seen = new Set<string>();
  const perTable = new Map<string, number>();
  const items: TableTemplate[] = [];
  for (const t of raw["items"] as unknown[]) {
    if (!isRecord(t) || typeof t["id"] !== "string" || t["id"] === "" || seen.has(t["id"])) continue;
    const table = t["table"];
    if (typeof table !== "string" || !TABLE_IDS.has(table)) continue;
    const def = defs[table as TableId];
    if (!def) continue;
    const name = typeof t["name"] === "string" ? cleanTemplateName(t["name"]) : "";
    if (!name || !Array.isArray(t["columns"])) continue;
    const n = perTable.get(table) ?? 0;
    if (n >= MAX_TEMPLATES_PER_TABLE) continue;
    const st = sanitizeColumnState({ columns: t["columns"], widths: t["widths"] }, def);
    seen.add(t["id"]);
    perTable.set(table, n + 1);
    items.push({ id: t["id"], table: table as TableId, name, columns: st.columns, widths: st.widths });
  }
  return { v: TABLE_TEMPLATES_VERSION, items };
}

export function templatesFor(doc: TableTemplatesDoc, table: TableId): TableTemplate[] {
  return doc.items.filter((t) => t.table === table);
}

/**
 * Save the state's columns and widths as `name` for `table`. An existing template of that table
 * with the same name (case-insensitive) is overwritten in place and keeps its id; otherwise a new
 * one with `newId` is appended. No-op on a blank name or at the per-table cap.
 */
export function saveTableTemplate(
  doc: TableTemplatesDoc,
  opts: { table: TableId; name: string; state: ColumnState; newId: string },
): { doc: TableTemplatesDoc; id: string | null } {
  const name = cleanTemplateName(opts.name);
  if (!name) return { doc, id: null };
  const body = { columns: [...opts.state.columns], widths: { ...opts.state.widths } };
  const existing = doc.items.find(
    (t) => t.table === opts.table && t.name.toLowerCase() === name.toLowerCase(),
  );
  if (existing) {
    return {
      doc: { ...doc, items: doc.items.map((t) => (t.id === existing.id ? { ...t, name, ...body } : t)) },
      id: existing.id,
    };
  }
  if (
    templatesFor(doc, opts.table).length >= MAX_TEMPLATES_PER_TABLE ||
    doc.items.some((t) => t.id === opts.newId)
  )
    return { doc, id: null };
  return {
    doc: { ...doc, items: [...doc.items, { id: opts.newId, table: opts.table, name, ...body }] },
    id: opts.newId,
  };
}

/** Rename; a blank name or one another template of the same table uses is refused (no-op). */
export function renameTableTemplate(doc: TableTemplatesDoc, id: string, name: string): TableTemplatesDoc {
  const clean = cleanTemplateName(name);
  const t = doc.items.find((x) => x.id === id);
  if (!clean || !t) return doc;
  const clash = doc.items.some(
    (x) => x.id !== id && x.table === t.table && x.name.toLowerCase() === clean.toLowerCase(),
  );
  if (clash) return doc;
  return { ...doc, items: doc.items.map((x) => (x.id === id ? { ...x, name: clean } : x)) };
}

export function deleteTableTemplate(doc: TableTemplatesDoc, id: string): TableTemplatesDoc {
  return doc.items.some((t) => t.id === id) ? { ...doc, items: doc.items.filter((t) => t.id !== id) } : doc;
}

/** Column state after applying a template or built-in (sort kept when its column is still shown). */
export function applyTableTemplate<R>(
  s: ColumnState,
  t: { id: string; columns: readonly string[]; widths?: Record<string, number> },
  def: TableDef<R>,
): ColumnState {
  const next = sanitizeColumnState({ columns: t.columns, widths: t.widths }, def);
  const sort = s.sort !== null && next.columns.includes(s.sort.id) ? s.sort : null;
  return { v: COLUMN_STATE_VERSION, columns: next.columns, widths: next.widths, sort, template: t.id };
}

/** The state's columns/widths differ from the template's (picker shows "modified"). */
export function differsFromTemplate(
  s: ColumnState,
  t: { columns: readonly string[]; widths?: Record<string, number> },
): boolean {
  if (s.columns.length !== t.columns.length || s.columns.some((c, i) => c !== t.columns[i])) return true;
  const a = Object.entries(s.widths);
  const b = t.widths ?? {};
  return a.length !== Object.keys(b).length || a.some(([k, w]) => b[k] !== w);
}
